// @flow

import React from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { compose } from "redux";
import { SectionComponent, Button, Pagination, TooltipDropdown } from "@components";
import type { DocumentType } from "@modules/shared/reducer/employee-events/types";
import actionCreators from "@modules/shared/actionCreator";
import * as selectors from "@modules/shared/selectors/employee-events";
import type { State } from "@store/reducers";
import type { PaginationType } from "@types";
import { DocumentsList, AddDocumentModal } from "@modules/shared/components";
import { parseSchema } from "@utils/schema";
import { unpatch } from "@transformers/js-client";

type OwnProps = $Exact<{
  employeeId: number,
  clientId: number,
}>;

type PropsFromState = $Exact<{
  documents: Array<DocumentType>,
  pagination: PaginationType,
  totalElements: number,
  isAddDocumentModalOpen: boolean,
}>;

type PropsFromDispatch = $Exact<{
  getDocuments: typeof actionCreators.GetEmployeeDocuments,
  onAddDocumentHandler: Function,
  onDismissAddDocumentHandler: Function,
  addDocument: typeof actionCreators.AddEmployeeDocument,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

type ComponentState = $Exact<{
  schema: ?Object,
  disableAddButton: boolean,
}>;

export class EmployeeDocumentsList extends React.Component<Props, ComponentState> {
  state = {
    schema: null,
    disableAddButton: false,
  };

  get pagination(): {
    pageNumber: number,
    totalPages: number,
  } {
    const totalPages = this.props.pagination.pageSize
      ? Math.ceil(this.props.totalElements / this.props.pagination.pageSize)
      : 0;

    return {
      pageNumber: this.props.pagination.pageNumber || 0,
      totalPages,
    };
  }

  componentDidMount() {
    this.fetchDocuments();

    if (!this.state.schema) {
      parseSchema("EmployeeFileDTO").then(schema =>
        this.setState({
          schema,
        }),
      );
    }
  }

  fetchDocuments = (nextPage?: number) => {
    this.props.getDocuments({
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
      page: nextPage,
    });
  };

  addDocument = (formData: Object) => {
    this.setState({
      disableAddButton: true,
    }),
      this.props.addDocument({
        formData: unpatch(formData),
        clientId: this.props.clientId,
        employeeId: this.props.employeeId,
      });
  };

  addDocumentHandler = () => {
    this.props.onAddDocumentHandler();
    this.setState({
      disableAddButton: false,
    });
  };

  render() {
    return (
      <SectionComponent
        wrapperClassName="flex-column"
        className="gs-container"
        title={
          <TooltipDropdown
            title={this.props.t("GENERAL_DOCUMENTS")}
            description={this.props.t("GENERAL_DOCUMENTS_DESCRIPTION")}
            id="general-documents-description"
          >
            <h5 className="mr-2">{this.props.t("GENERAL_DOCUMENTS")}</h5>
          </TooltipDropdown>
        }
        rightLink={
          <Button materialIcon="upload_file" onClick={this.addDocumentHandler}>
            {this.props.t("UPLOAD")}
          </Button>
        }
      >
        {this.state.schema && this.props.isAddDocumentModalOpen && (
          <AddDocumentModal
            onDismissHandler={this.props.onDismissAddDocumentHandler}
            onSubmit={this.addDocument}
            schema={this.state.schema}
            disabledButton={this.state.disableAddButton}
          />
        )}
        <DocumentsList
          data={this.props.documents}
          employeeId={this.props.employeeId}
          clientId={this.props.clientId}
        />
        <Pagination
          className="mx-auto mt-3 d-block"
          currentPage={this.pagination.pageNumber}
          totalPages={this.pagination.totalPages}
          onChange={this.fetchDocuments}
        />
      </SectionComponent>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => ({
  documents: selectors.getDocuments(state),
  pagination: selectors.getDocumentsPagination(state),
  totalElements: selectors.getDocumentsTotalElements(state),
  isAddDocumentModalOpen: selectors.getIsAddDocumentModalOpen(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  getDocuments: actionCreators.GetEmployeeDocuments,
  onAddDocumentHandler: () => actionCreators.ToggleAddEmployeeDocumentModal(true),
  onDismissAddDocumentHandler: () => actionCreators.ToggleAddEmployeeDocumentModal(false),
  addDocument: actionCreators.AddEmployeeDocument,
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  localize(),
)(EmployeeDocumentsList);
